import { DeviceDetails } from './asset-details';

type LifecycleState = 'InProgress' | 'Approval' | 'Released' | 'Deprecated' | 'Withdrawn';
export type DeploymentType = 'scheduled' | 'manually';
export type DeploymentStatus = 'pending' | 'started' | 'complete' | 'cancelled' | 'error';

interface RawSoftwarePackage {
  id: string;
  name: string;
  version: string;
  versionLabel?: string;
  packageLoaderId: string;
  creationDate: string;
  size: number;
  lifecycleState: LifecycleState;
}

export interface MultipleSoftwarePackagesResponse {
  data: RawSoftwarePackage[];
}

type ConvertedPackage = Omit<RawSoftwarePackage, 'creationDate'> & { creationDate: Date };

export class SoftwarePackage implements ConvertedPackage {
  readonly id: string;
  readonly name: string;
  readonly version: string;
  readonly versionLabel?: string;
  readonly packageLoaderId: string;
  readonly creationDate: Date;
  readonly size: number;
  readonly lifecycleState: LifecycleState;

  constructor(packageResponse: RawSoftwarePackage) {
    Object.assign(this, {
      ...packageResponse,
      creationDate: new Date(packageResponse.creationDate),
    });
  }

  get officialVersion() {
    return this.versionLabel || this.version;
  }

  hasVersion(version: string) {
    return this.versionLabel === version || this.version === version;
  }
}

export enum ConditionTypes {
  WEBHOOK = 'webhook',
}

export type DeploymentCondition = {
  type: ConditionTypes.WEBHOOK;
  url: string;
  properties?: {
    method?: string;
    body?: unknown;
    parameters?: object;
    headers?: object;
  };
  expectedResults?: {
    status?: number;
    body?: unknown;
    headers?: object;
  };
};

interface RawDeploymentTask {
  id: string;
  pid: string;
  packageId: string;
  status: DeploymentStatus | 'processing';
  result?: string;
  description?: string;
  cancelable: boolean;
  targetFirmwareVersion: string;
  targetFirmwareVersionLabel?: string;
  sourceFirmwareVersion?: string;
  sourceFirmwareVersionLabel?: string;
  createdDate: string;
  updatedDate: string;
  finishedDate?: string;
  conditions?: DeploymentCondition[];
  systemThingId?: string;
  progress: number;
  thingId: string;
  thingDescriptionDirectoryUrl: string;
  type: DeploymentType;
  deployDate?: string;
}

export interface MultipleDeploymentTasksResponse {
  data: RawDeploymentTask[];
}

export interface SingleDeploymentTaskResponse {
  data: RawDeploymentTask;
}

type ConvertedDeploymentTask = Omit<
  RawDeploymentTask,
  'createdDate' | 'updatedDate' | 'finishedDate' | 'deployDate'
> & {
  createdDate: Date;
  updatedDate: Date;
  finishedDate?: Date;
  deployDate?: Date;
};

export class DeploymentTask implements ConvertedDeploymentTask {
  id: string;
  pid: string;
  packageId: string;
  status: DeploymentStatus;
  result?: string;
  description?: string;
  cancelable: boolean;
  targetFirmwareVersion: string;
  targetFirmwareVersionLabel?: string;
  sourceFirmwareVersion?: string;
  sourceFirmwareVersionLabel?: string;
  createdDate: Date;
  updatedDate: Date;
  finishedDate?: Date;
  deployDate?: Date;
  conditions?: DeploymentCondition[];
  systemThingId?: string;
  progress: number;
  thingId: string;
  thingDescriptionDirectoryUrl: string;
  type: DeploymentType;

  constructor(deploymentTaskResponse: RawDeploymentTask) {
    Object.assign(this, {
      ...deploymentTaskResponse,
      status:
        deploymentTaskResponse.status === 'processing' ? 'started' : deploymentTaskResponse.status,
      createdDate: new Date(deploymentTaskResponse.createdDate),
      updatedDate: new Date(deploymentTaskResponse.updatedDate),
      finishedDate: deploymentTaskResponse.finishedDate
        ? new Date(deploymentTaskResponse.finishedDate)
        : undefined,
      deployDate: deploymentTaskResponse.deployDate
        ? new Date(deploymentTaskResponse.deployDate)
        : undefined,
    });
  }

  get officialTargetVersion() {
    return this.targetFirmwareVersionLabel || this.targetFirmwareVersion;
  }

  get officialSourceVersion() {
    return this.sourceFirmwareVersionLabel || this.sourceFirmwareVersion;
  }
}

export type DeviceInfo = Pick<DeviceDetails, 'partNumber' | 'serialNumber'>;

export interface Auth {
  user: string;
  password: string;
}

export interface CreateDeploymentTask {
  thingId: string;
  deviceInfo: DeviceInfo;
  packageId: string;
  thingDescriptionDirectoryUrl: string;
  auth: Auth;
  description?: string;
  serviceThingId?: string;
  type?: DeploymentType;
  deployDate?: Date;
  conditions?: DeploymentCondition[];
}

export interface DeployedFirmware {
  package: SoftwarePackage;
  deployment?: DeploymentTask;
}

export class TaskNotCancelableError {}

const PENDING_TASK_TRACK_LIMIT_MS = 5 * 60 * 1000;

export function isScheduledSoon(deploymentTask: DeploymentTask) {
  if (!deploymentTask.deployDate) {
    return false;
  }
  const pendingTaskMaxTime = new Date().getTime() + PENDING_TASK_TRACK_LIMIT_MS;
  return deploymentTask.deployDate.getTime() <= pendingTaskMaxTime;
}

export function canTrackTask(deploymentTask: DeploymentTask) {
  return (
    (deploymentTask.status === 'pending' && isScheduledSoon(deploymentTask)) ||
    deploymentTask.status === 'started'
  );
}
